// src/services/serviceMainCategoriesService.js
//
// Frontend API wrapper for Service Main Categories in the Catalog domain.
//
// PURPOSE
// -------
// This service handles the top-level service category layer used by employees
// inside hb_visits, for example:
//
// - Hairdressing
// - Cosmetology
// - Spa & Relax
// - Health
//
// Goals
// -----
// 1) Keep network calls in one place.
// 2) Use clear, domain-friendly function names.
// 3) Avoid noisy console logging (especially for success paths).
// 4) Provide consistent error handling and messages.
// 5) Keep request contracts documented close to the API surface.
//
// Logging policy (recommended)
// ----------------------------
// - Service layer should NOT spam console.log on success.
// - Service layer may log errors ONLY when a debug flag is enabled.
// - UI layer is responsible for user-facing messages and UX.
//
// Enable debug logs locally:
//   REACT_APP_DEBUG_API=true
//

import apiClient from "../utils/apiClient";

// Toggle error logging without changing code.
// In CRA / Vite, env values are strings.
const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

/**
 * Internal: extract a safe, readable message from Axios errors and backend bodies.
 * We keep this lightweight and consistent across service methods.
 */
function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

/**
 * Internal: build and throw a normalized Error that callers can handle.
 * Adds useful metadata without leaking too much.
 */
function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  // Attach diagnostics for programmatic handling (optional).
  e.status = err?.response?.status;
  e.data = err?.response?.data;

  // Debug log only when explicitly enabled.
  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[ServiceMainCategoriesService] ${context}`, {
      status: e.status,
      message,
      data: e.data,
    });
  }

  throw e;
}

/**
 * Fetch service main categories.
 *
 * Backend:
 *   GET /catalog/service-main-categories
 *
 * Optional query params:
 *   - active=true|false
 *
 * Example:
 *   fetchServiceMainCategories()
 *   fetchServiceMainCategories({ active: true })
 *
 * Return:
 * - expected: array of service main category objects
 * - defensive fallback: [] if backend payload is missing / malformed
 */
export const fetchServiceMainCategories = async (params = {}) => {
  try {
    const res = await apiClient.get("/catalog/service-main-categories", {
      withCredentials: true,
      params,
    });

    const payload = res?.data;
    return Array.isArray(payload) ? payload : [];
  } catch (err) {
    throwApiError("fetchServiceMainCategories", err);
  }
};

/**
 * Create a new service main category.
 *
 * Backend:
 *   POST /catalog/service-main-categories
 *
 * Expected body:
 *   {
 *     name_internal: string,
 *     name_public: string,
 *     slug?: string,
 *     description_short?: string,
 *     description_long?: string,
 *     sort_order?: number,
 *     is_active?: boolean,
 *     publish_to_website?: boolean
 *   }
 *
 * Notes:
 * - name_internal and name_public are required from business perspective.
 * - slug may be auto-generated by backend if omitted, depending on backend rules.
 */
export const createServiceMainCategory = async (payload) => {
  try {
    if (!payload || typeof payload !== "object") {
      const e = new Error("createServiceMainCategory: payload object is required.");
      e.code = "SMC_PAYLOAD_MISSING";
      throw e;
    }

    if (!payload.name_internal || `${payload.name_internal}`.trim() === "") {
      const e = new Error("createServiceMainCategory: name_internal is required.");
      e.code = "SMC_NAME_INTERNAL_MISSING";
      throw e;
    }

    if (!payload.name_public || `${payload.name_public}`.trim() === "") {
      const e = new Error("createServiceMainCategory: name_public is required.");
      e.code = "SMC_NAME_PUBLIC_MISSING";
      throw e;
    }

    const res = await apiClient.post("/catalog/service-main-categories", payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("createServiceMainCategory", err);
  }
};

/**
 * Update an existing service main category.
 *
 * Backend:
 *   PUT /catalog/service-main-categories/:id
 *
 * Notes:
 * - Full update contract is assumed (not JSON Patch).
 * - Caller should pass the complete edited payload expected by backend.
 */
export const updateServiceMainCategory = async (id, payload) => {
  try {
    if (id === undefined || id === null || `${id}`.trim() === "") {
      const e = new Error("updateServiceMainCategory: id is required.");
      e.code = "SMC_ID_MISSING";
      throw e;
    }

    if (!payload || typeof payload !== "object") {
      const e = new Error("updateServiceMainCategory: payload object is required.");
      e.code = "SMC_PAYLOAD_MISSING";
      throw e;
    }

    const res = await apiClient.put(`/catalog/service-main-categories/${id}`, payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("updateServiceMainCategory", err);
  }
};

/**
 * Toggle active state of a service main category.
 *
 * Backend:
 *   PATCH /catalog/service-main-categories/:id/active
 *   Body: { active: true|false }
 *
 * Notes:
 * - We keep this separate from full update because active/inactive operations
 *   are often triggered directly from list actions in the UI.
 */
export const toggleServiceMainCategoryActive = async (id, active) => {
  try {
    if (id === undefined || id === null || `${id}`.trim() === "") {
      const e = new Error("toggleServiceMainCategoryActive: id is required.");
      e.code = "SMC_ID_MISSING";
      throw e;
    }

    const res = await apiClient.patch(
      `/catalog/service-main-categories/${id}/active`,
      { active: !!active },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("toggleServiceMainCategoryActive", err);
  }
};

/**
 * Delete a service main category.
 *
 * Backend:
 *   DELETE /catalog/service-main-categories/:id
 *
 * IMPORTANT
 * ---------
 * Delete button may always be visible in the UI,
 * but backend may reject the operation if:
 * - the record has publication history
 * - the record still has dependent child sections
 */
export const deleteServiceMainCategory = async (id) => {
  try {
    if (id === undefined || id === null || `${id}`.trim() === "") {
      const e = new Error("deleteServiceMainCategory: id is required.");
      e.code = "SMC_ID_MISSING";
      throw e;
    }

    const res = await apiClient.delete(`/catalog/service-main-categories/${id}`, {
      withCredentials: true,
    });

    return res?.data ?? { ok: true };
  } catch (err) {
    throwApiError("deleteServiceMainCategory", err);
  }
};

/**
 * Optional default export (consistent with other services if preferred).
 */
export default {
  fetchServiceMainCategories,
  createServiceMainCategory,
  updateServiceMainCategory,
  toggleServiceMainCategoryActive,
  deleteServiceMainCategory,
};